import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import "../../landing.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8085";

const PAIN_POINTS = [
  { icon: "💭", h: "The idea sits in your head", p: "You have the thought. Turning it into a post feels like effort — so it never happens." },
  { icon: "😶", h: "You don't know what you actually think", p: "Until someone asks the right questions. Most AI tools generate text. They don't help you find your POV." },
  { icon: "🔁", h: "You have the post. Then what?", p: "A wall of text gets skipped. A well-designed carousel gets shared. The gap is the design work — and that's what we eliminate." },
  { icon: "🤖", h: "AI copy sounds like AI copy", p: "Generic, polished, voiceless. It doesn't sound like you — so you don't post it anyway." },
];

const WHO = ["Founders", "Consultants", "Operators", "Marketers", "Creators", "PMs", "Coaches", "Recruiters", "Experts"];

const FEED_TOPICS = ["All", "Leadership", "Productivity", "Strategy", "Career", "Marketing", "Founder Life"];
const FEED_PERIODS: { label: string; value: string }[] = [
  { label: "Today",     value: "today" },
  { label: "This week", value: "week"  },
  { label: "All time",  value: "all"   },
];

interface ActivityData {
  launchDate: string;
  totalUsers: number;
  totalCarousels: number;
  registeredUsers: number;
  earlyBirdUsed: number;
  earlyBirdLimit: number;
  hourly: Array<{ hour: string; count: number }>;
}

interface FeedInsight {
  token: string;
  distilledInsight: string;
  topic?: string;
  toneTags?: string[];
  hook?: string;
  likes: number;
  viewCount: number;
}

const LandingPage: React.FC = () => {
  const history = useHistory();

  // ── Hero CTA ─────────────────────────────────────────────────────────
  const [thought, setThought] = useState("");

  // ── Sample carousel PDF viewer ────────────────────────────────────────
  const [pdfDoc, setPdfDoc]         = useState<any>(null);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [totalSlides, setTotalSlides]   = useState(0);
  const pdfCanvasRef = useRef<HTMLCanvasElement>(null);
  const pdfRenderRef = useRef<any>(null);

  // ── Live activity data (pricing + proof sections) ─────────────────────
  const [activityData, setActivityData] = useState<ActivityData | null>(null);

  // ── Discovery feed ────────────────────────────────────────────────────
  const [feedInsights, setFeedInsights] = useState<FeedInsight[]>([]);
  const [feedTopic, setFeedTopic] = useState("All");
  const [feedPeriod, setFeedPeriod] = useState("all");
  const [feedLoading, setFeedLoading] = useState(true);

  // ── Effects ───────────────────────────────────────────────────────────
  useEffect(() => {
    axios.get(`${API_URL}/api/stats/activity`)
      .then(r => setActivityData(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-up").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Load sample PDF
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        (pdfjsLib as any).GlobalWorkerOptions.workerSrc =
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${(pdfjsLib as any).version}/pdf.worker.min.js`;
        const doc = await (pdfjsLib as any).getDocument('/sample-carousel.pdf').promise;
        if (!cancelled) { setPdfDoc(doc); setTotalSlides(doc.numPages); }
      } catch { /* non-critical — card degrades gracefully */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Render current slide to canvas
  useEffect(() => {
    if (!pdfDoc || !pdfCanvasRef.current) return;
    let cancelled = false;
    (async () => {
      try {
        if (pdfRenderRef.current) { pdfRenderRef.current.cancel(); pdfRenderRef.current = null; }
        const page  = await pdfDoc.getPage(currentSlide);
        if (cancelled) return;
        const canvas = pdfCanvasRef.current!;
        const contW  = canvas.parentElement?.clientWidth ?? 280;
        const dpr    = window.devicePixelRatio || 1;
        const baseVp = page.getViewport({ scale: 1 });
        const scale  = (contW / baseVp.width) * dpr;
        const vp     = page.getViewport({ scale });
        canvas.width  = vp.width;
        canvas.height = vp.height;
        const task = page.render({ canvasContext: canvas.getContext('2d')!, viewport: vp });
        pdfRenderRef.current = task;
        await task.promise;
      } catch (e: any) {
        if (e?.name !== 'RenderingCancelledException') console.error('[PDF]', e);
      }
    })();
    return () => { cancelled = true; };
  }, [pdfDoc, currentSlide]);

  useEffect(() => {
    setFeedLoading(true);
    const params = new URLSearchParams({ limit: '9', page: '1' });
    if (feedTopic !== "All") params.set('topic', feedTopic);
    if (feedPeriod !== "all") params.set('period', feedPeriod);
    axios
      .get(`${API_URL}/api/share/feed?${params}`)
      .then((r) => setFeedInsights(r.data))
      .catch(() => setFeedInsights([]))
      .finally(() => setFeedLoading(false));
  }, [feedTopic, feedPeriod]);

  // ── Helpers ───────────────────────────────────────────────────────────
  const handleTryIt = () => {
    const text = thought.trim();
    if (!text) return;
    history.push(`/distill-v2?q=${encodeURIComponent(text)}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTryIt();
    }
  };

  // ─────────────────────────────────────────────────────────────────────
  return (
    <div className="landing">
      {/* Nav — marketing only: logo + Go to app */}
      <nav>
        <div className="nav-logo">
          <img src="/favicon.svg" alt="" width="22" height="22" style={{ display: 'block', flexShrink: 0 }} />
          distill<sup>beta</sup>
        </div>
        <div className="nav-right">
          <a className="nav-pill" href="/distill-v2">Go to app →</a>
        </div>
      </nav>

      {/* ── Hero — product showcase ── */}
      <section className="hero">
        <h1><em>LinkedIn carousel</em><br />in 30 seconds.</h1>
        <p className="hero-sub">
          Type your rough thought. No design skills. No Canva. No blank page.
        </p>

        {/* 2-step visual — input → carousel */}
        <div className="showcase-cards">

          {/* Step 1 — rough thought */}
          <div className="showcase-card">
            <p className="showcase-step">Step 1</p>
            <div className="sc-input">
              <div className="sc-input-body">
                4 habits which are must have for founders
              </div>
            </div>
            <p className="showcase-step-label">Drop your raw thought</p>
          </div>

          <div className="showcase-arrow">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 14H22M22 14L15 7M22 14L15 21" stroke="#B0A8F0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          {/* Step 2 — real PDF carousel */}
          <div className="showcase-card">
            <p className="showcase-step">Step 2</p>
            <div className="sc-pdf-wrap">
              {pdfDoc ? (
                <canvas ref={pdfCanvasRef} className="sc-pdf-canvas" />
              ) : (
                <div className="sc-pdf-loading">
                  <div className="sc-pdf-spinner" />
                </div>
              )}
              <div className="sc-pdf-nav">
                <button
                  className="sc-pdf-nav-btn"
                  onClick={() => setCurrentSlide(s => Math.max(1, s - 1))}
                  disabled={currentSlide <= 1}
                >‹</button>
                <span className="sc-pdf-nav-num">{currentSlide} / {totalSlides || '…'}</span>
                <button
                  className="sc-pdf-nav-btn"
                  onClick={() => setCurrentSlide(s => Math.min(totalSlides, s + 1))}
                  disabled={currentSlide >= totalSlides}
                >›</button>
                <span className="sc-pdf-nav-spacer" />
                <a
                  href="/sample-carousel.pdf"
                  download="distill-sample-carousel.pdf"
                  className="sc-pdf-download"
                >↓ Download</a>
              </div>
            </div>
            <p className="showcase-step-label">Your carousel, ready to post</p>
          </div>

        </div>

        {/* Social proof quote */}
        <div className="hero-testimonial">
          <div className="hero-testimonial-quote">
            "Impressed by the designs. Needed a bit of editing later but it saved me 2&nbsp;hrs of creating from scratch."
          </div>
          <div className="hero-testimonial-author">Aditi Bandewar</div>
        </div>

        {/* Try It Out inline CTA */}
        <div className="hero-tryit">
          <p className="hero-tryit-label">Try it now — free</p>
          <div className="hero-tryit-wrap">
            <textarea
              className="hero-tryit-input"
              placeholder="Type your rough thought here… e.g. most founders confuse being busy with making progress"
              value={thought}
              onChange={(e) => setThought(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={3}
              data-testid="mkt-thought-input"
            />
            <button
              className="hero-tryit-btn"
              onClick={handleTryIt}
              disabled={!thought.trim()}
              data-testid="mkt-try-btn"
            >
              Try It Out →
            </button>
          </div>
        </div>

      </section>

      {/* ── Pricing ── */}
      <section className="pricing-section fade-up" id="pricing">
        <div className="pricing-badge">
          <span className="pricing-badge-dot" />
          Launch pricing — next 48 hours only
        </div>
        <h2 className="pricing-title">Simple, per-carousel pricing.<br /><em>No subscription.</em></h2>
        <p className="pricing-sub">Pay for what you use. First 100 early adopters get 1 month free.</p>

        <div className="pricing-grid">
          {/* Early Adopter */}
          {(() => {
            const used      = activityData?.earlyBirdUsed  ?? 0;
            const limit     = activityData?.earlyBirdLimit ?? 100;
            const spotsLeft = limit - used;
            const open      = spotsLeft > 0;
            return (
              <div className={`pricing-card${open ? ' pricing-card--featured' : ''}`}>
                <div className="pricing-card-badge">
                  {open ? `🎉 ${spotsLeft} of ${limit} spots left` : 'Early bird full'}
                </div>
                <div className="pricing-card-name">Early Adopter</div>
                <div className="pricing-card-price">Free</div>
                <div className="pricing-card-desc">
                  {open
                    ? 'Unlimited carousels for your first month. No card needed.'
                    : 'All early adopter spots have been claimed.'}
                </div>
                {open && (
                  <button className="pricing-card-cta" onClick={() => history.push('/distill-v2')}>
                    Generate your first carousel ↓
                  </button>
                )}
              </div>
            );
          })()}

          {/* Launch Rate */}
          <div className="pricing-card">
            <div className="pricing-card-badge">⏰ 48 hrs only</div>
            <div className="pricing-card-name">Launch Rate</div>
            <div className="pricing-card-price">₹1 <span className="pricing-card-per">/ carousel</span></div>
            <div className="pricing-card-desc">After your free trial. Pay per download. No subscription.</div>
            <div className="pricing-card-bundle">Buy 10 → ₹8 each (save 20%)</div>
          </div>

          {/* Standard */}
          <div className="pricing-card pricing-card--muted">
            <div className="pricing-card-badge">After launch</div>
            <div className="pricing-card-name">Standard</div>
            <div className="pricing-card-price">₹11 <span className="pricing-card-per">/ carousel</span></div>
            <div className="pricing-card-desc">Full access. Pay as you go. Bundles available.</div>
            <div className="pricing-card-bundle">Buy 10 → ₹88 (save 20%)</div>
          </div>
        </div>

        <p className="pricing-note">Payment via UPI. Activate instantly — scan, pay, paste transaction ID. No waitlist.</p>
      </section>

      {/* ── Proof ── */}
      <section className="proof-section">
        <div className="section-label">Built for people who think but don't post enough</div>

        {/* Live activity graph */}
        <div className="activity-graph-wrap fade-up">
          <div className="activity-graph-header">
            <span className="activity-graph-title">
              <span className="activity-live-dot" /> Live since launch
            </span>
            <span className="activity-graph-stats">
              {activityData ? (
                <><strong>{activityData.totalUsers}</strong> users · <strong>{activityData.totalCarousels}</strong> carousels</>
              ) : (
                <span style={{ opacity: 0.4 }}>Loading…</span>
              )}
            </span>
          </div>
          <div className="activity-graph-bars">
            {(activityData?.hourly?.length ?? 0) > 0 ? (() => {
              const hourly = activityData!.hourly;
              const max    = Math.max(...hourly.map(h => h.count), 1);
              const bars   = hourly.slice(-24);
              return bars.map((h, i) => (
                <div
                  key={i}
                  className="activity-bar-wrap"
                  title={`${new Date(h.hour).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}: ${h.count} carousels`}
                >
                  <div
                    className={`activity-bar${h.count > 0 ? ' activity-bar--active' : ''}`}
                    style={{ height: `${Math.max(4, Math.round((h.count / max) * 56))}px` }}
                  />
                </div>
              ));
            })() : (
              Array.from({ length: 12 }).map((_, i) => (
                <div key={i} className="activity-bar-wrap">
                  <div className="activity-bar" style={{ height: '4px' }} />
                </div>
              ))
            )}
          </div>
          <div className="activity-graph-xlabel">
            ← Launch{activityData ? ` · ${new Date(activityData.launchDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}` : ''} · Now →
          </div>
        </div>

        <div className="section-label">Does any of this sound familiar?</div>
        <div className="pain-grid">
          {PAIN_POINTS.map((item, i) => (
            <div key={i} className="pain-card">
              <div className="pain-icon">{item.icon}</div>
              <div className="pain-h">{item.h}</div>
              <div className="pain-p">{item.p}</div>
            </div>
          ))}
        </div>

        <div className="section-label">Who it's for</div>
        <div className="who-row">{WHO.map((w) => <div key={w} className="who-chip">{w}</div>)}</div>

        <div className="testimonial-card">
          <div className="t-text">"I had a vague idea at 7am and had a LinkedIn post, X thread, and email draft by 7:15. That's insane."</div>
          <div className="t-name">Rohan M.</div>
          <div className="t-role">Founder, B2B SaaS</div>
        </div>
      </section>

      {/* ── Community discovery feed ── */}
      <section className="discover-section fade-up">
        <div className="discover-inner">
          <div className="discover-header">
            <div className="section-label">Insights from the community</div>
            <div className="discover-period-row">
              {FEED_PERIODS.map((p) => (
                <button
                  key={p.value}
                  className={`discover-period-btn${feedPeriod === p.value ? " active" : ""}`}
                  onClick={() => setFeedPeriod(p.value)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="discover-topic-row">
            {FEED_TOPICS.map((t) => (
              <button
                key={t}
                className={`discover-topic-btn${feedTopic === t ? " active" : ""}`}
                onClick={() => setFeedTopic(t)}
              >
                {t}
              </button>
            ))}
          </div>

          {feedLoading ? (
            <div className="discover-loading">
              <div className="discover-spinner" />
            </div>
          ) : feedInsights.length === 0 ? (
            <div className="discover-empty">
              No insights yet for this filter — be the first to distill one.
            </div>
          ) : (
            <div className="discover-grid">
              {feedInsights.map((ins) => (
                <a
                  key={ins.token}
                  className="discover-card"
                  href={`/share/${ins.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="dc-quote">"</div>
                  <div className="dc-text">{ins.distilledInsight}</div>
                  <div className="dc-footer">
                    <div className="dc-meta">
                      {ins.topic && <span className="dc-topic">{ins.topic}</span>}
                      {(ins.toneTags ?? []).slice(0, 2).map((t) => (
                        <span key={t} className="dc-tag">{t}</span>
                      ))}
                    </div>
                    <div className="dc-stats">
                      {ins.likes > 0 && <span className="dc-likes">👍 {ins.likes}</span>}
                      <span className="dc-read">Read →</span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="final-cta fade-up">
        <h2>Stop spending an hour<br />in <em>Canva.</em></h2>
        <p className="sub">Type your rough thought. Get a LinkedIn carousel in 30 seconds.</p>
        <button className="final-btn" onClick={() => history.push('/distill-v2')}>
          Generate your first carousel — free →
        </button>
        <div className="final-note">No card. No sign-up. Just type and go.</div>
      </section>

      <footer>
        <div className="footer-logo">distill<sup>beta</sup></div>
        <div className="footer-note">© 2025 Distill. Built for people who think.</div>
      </footer>
    </div>
  );
};

export default LandingPage;
